import { Box, HeadingText } from "@leagueplatform/genesis-core";
import {
  HIDDEN_SUBTREE_CLASS_NAME,
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
  useIsAncestrallyHidden,
} from "@leagueplatform/masonry-engine";

/**
 * Custom renderer for `linearFlowStep` nodes. Wraps each step's items in a
 * white card matching the Quest Feature Highlight design, leaving the
 * surrounding `linearFlow` navigator (progress bar + back / next buttons)
 * rendered against the page's gray background.
 *
 * Quest currently uses `linearFlow` only for the Feature Highlights flow,
 * so registering this globally is fine. If another linear flow lands and
 * needs different chrome, gate the styling by node id / namespace.
 */
type LinearFlowStepProperties = {
  heading?: string;
  headingLevel?: "1" | "2" | "3" | "4";
  headingSize?: "sm" | "md" | "lg" | "xl";
};

type LinearFlowStepSections = {
  items: MasonryEngineNode[];
};

type LinearFlowStepNode = MasonryEngineNode<
  "linearFlowStep",
  LinearFlowStepProperties,
  Record<string, never>,
  LinearFlowStepSections
>;

type LinearFlowStepRendererProps =
  MasonryEngineNodeRendererProps<LinearFlowStepNode>;

export const MasonryFeatureHighlightStepRenderer = ({
  heading,
  headingLevel = "3",
  headingSize = "lg",
  items,
}: LinearFlowStepRendererProps) => {
  const isHidden = useIsAncestrallyHidden();
  return (
    <Box
      className={isHidden ? HIDDEN_SUBTREE_CLASS_NAME : undefined}
      css={{
        background: "$surfaceBackgroundPrimary",
        borderRadius: "$card",
        boxShadow: "$card",
        padding: "$two",
        // Stretch the step card so the surrounding navigator gets pushed
        // to the bottom of the gray viewport region. The reserved space
        // accounts for the navbar, outer page padding, and navigator.
        // We intentionally do NOT use flex column here — the BE's
        // verticalLayout uses StackItem verticalAlignment:"bottom", which
        // would push the body to the bottom of the card if we did.
        minHeight: "calc(100vh - 240px)",
      }}
    >
      {heading && (
        <HeadingText level={headingLevel} size={headingSize}>
          {heading}
        </HeadingText>
      )}
      {items}
    </Box>
  );
};
